import { EdpUser } from "@edp-models/user.model";

export class Address {
    constructor(
        public Street: string,
        public HouseNumber: string,
        public Supplement: string,
        public PostalCode: string,
        public City: string,
        public NumberComplement?: string
    ) {}

    public toString = (): string => {
        return `${this.Street} ${[this.HouseNumber, this.NumberComplement, this.Supplement].filter(Boolean).join(", ")} ${this.PostalCode} ${this.City}`;
    };
}

export class AddressFactory {
    static create(address: Address) {
        return new Address(
            address.Street,
            address.HouseNumber,
            address.Supplement,
            address.PostalCode,
            address.City,
            address.NumberComplement
        );
    }
}

export class BasicComponent {
    constructor(
        public DeliveryPoint: string,
        public SupplyType: string,
        public Tariff: string,
        public Address: Address,
        public VoltageLevel: string,
        public Power: string,
        public Cycle: string
    ) {}
}

export class BasicComponentFactory {
    static create(basicComponent: BasicComponent) {
        return new BasicComponent(
            basicComponent.DeliveryPoint,
            basicComponent.SupplyType,
            basicComponent.Tariff,
            basicComponent.Address,
            basicComponent.VoltageLevel,
            basicComponent.Power,
            basicComponent.Cycle
        );
    }
}

export interface Attributes {
    [name: string]: string;
}

export class Product {
    constructor(
        public Description: string,
        public InsertDate: string,
        public ProductCodes: string[],
        public ProductId: string,
        public Status: string,
        public StatusDescription: string,
        public attributes: Attributes
    ) {}
}

export class ProductFactory {
    static create(product: Product) {
        return new Product(
            product.Description,
            product.InsertDate,
            product.ProductCodes,
            product.ProductId,
            product.Status,
            product.StatusDescription,
            product.attributes
        );
    }
}

export class BillingAddressModel {
    constructor(
        public City: string,
        public Country: string,
        public HouseNumber: string,
        public Location: string,
        public NumberComplement: string,
        public PostalCode: string,
        public Street: string,
        public Supplement: string
    ) {}
}

export class HasPackModel {
    constructor(public easy: boolean, public smart: boolean, public full: boolean, public full_sm: boolean) {}
}

export class HasPackFactory {
    static create(hasPack: HasPackModel) {
        return new HasPackModel(hasPack.easy, hasPack.smart, hasPack.full, hasPack.full_sm);
    }
}

export interface IPackCode {
    code: string;
    status: string;
    date: string;
}

export class PackCodeModel {
    constructor(public easy: IPackCode, public smart: IPackCode, public full: IPackCode) {}
}

export class PackCodeFactory {
    static create(packCode: PackCodeModel) {
        return new PackCodeModel(packCode.easy, packCode.smart, packCode.full);
    }
}

export class BillingAddressFactory {
    static create(billingAddress: BillingAddressModel) {
        return new BillingAddressModel(
            billingAddress.City,
            billingAddress.Country,
            billingAddress.HouseNumber,
            billingAddress.Location,
            billingAddress.NumberComplement,
            billingAddress.PostalCode,
            billingAddress.Street,
            billingAddress.Supplement
        );
    }
}

export interface ContractManager {
    AccountID: string;
    UserID: string;
    Nif: string;
    FirstName: string;
    LastName: string;
}

export interface IPropertyType {
    name: string;
    path?: string;
    iconBop?: string;
    iconBra?: string;
    visible: boolean;
    active?: string;
}

export interface CO2Info {
    TotalCO2Avoided: number;
    TotalSavedTrees: number;
}

export interface SmartNow {
    ActivationDate: string;
    ConfigurationDate: string;
    HasSmartNow: boolean;
    Id: string;
    IsActive: boolean;
    Simulation: SmartNowSimulation;
    LastReadingE: string;
    LastReadingG: string;
}

export interface SmartNowSimulation {
    ConsumptionE: string;
    ConsumptionG: string;
    End: string;
    Start: string;
    ValueE: string;
    ValueG: string;
    ValueTotal: string;
    Ird_start: string;
    Ird_end: string;
}

export interface ContractOperation {
    id: number;
    value: string;
    operation: string;
}

export class Contract {
    constructor(
        public Avatar: string,
        public CustomerFirstName: string,
        public CustomerLastName: string,
        public ElectronicInvoiceEmail: string,
        public DirectDebitIban: string,
        public Company: string,
        public Nickname: string,
        public ResourceId: string,
        public AccountId: string,
        public BasicComponent: BasicComponent[],
        public Product: Product[],
        public HasE: boolean,
        public HasG: boolean,
        public HasServiceFunciona: boolean,
        public OfferId: string,
        public edpPlus: boolean,
        public HasDirectDebit: boolean,
        public HasDynamicOfferE: boolean,
        public HasElectronicInvoice: boolean,
        public ContractId: string,
        public ContractNr: string,
        public IsManaged: boolean,
        public OwnerNif: string,
        public PhoneNumber: string,
        public HasContaCerta: boolean,
        public ContaCertaValue: string,
        public HasServiceFaturaSegura: boolean,
        public Disabled: boolean,
        public BillingAddress: BillingAddressModel,
        public Status: string,
        public HasPack: HasPackModel,
        public PackCode: PackCodeModel,
        public HasPackInActivation: boolean,
        public Attributes: { [key: string]: string },
        public HasM: boolean,
        public HasServiceFuncionaCA: boolean,
        public AccountNumber: string,
        public EmployeeBenefit: boolean,
        public IsManagedBy: any,
        public IsManagedByMe: boolean,
        public HasHealth: boolean,
        public HasIndexedOfferE: boolean,
        public HasIndexedOfferG: boolean,
        public HasSolar: boolean,
        public Ird_start: string,
        public Ird_end: string,
        public Ird_update: string,
        public BankName: string,
        public CO2Info: CO2Info,
        public HasRedy: boolean,
        public SmartNow: SmartNow,
        public Telemetering: boolean,
        public UpdateInProgress: boolean,
        public CreationDate: string,
        public InvoicePeriodicity: string,
        public ContractOperation: ContractOperation[],
        public Mandate: Mandate[]
    ) {}
    public getBasicComponentByType(type: string): BasicComponent {
        return this.BasicComponent.find(t => t.SupplyType === type);
    }

    public getBasicComponentAddress(index: number): Address {
        return this.BasicComponent[index].Address;
    }

    public getInstallationAddress(): Address {
        return this.BasicComponent[0].Address;
    }

    public getBasicComponentCpe(): string {
        return this.BasicComponent[0].DeliveryPoint;
    }

    public getEnergyCPE(): string {
        return this.BasicComponent.filter((item) => item.SupplyType == "E")[0].DeliveryPoint;
    }

    public getType(): string {
        if (this.HasE && !this.HasG) {
            return "electricity";
        } else if (!this.HasE && this.HasG) {
            return "gas";
        } else if (this.HasE && this.HasG) {
            return "dual";
        } else {
            return "funciona";
        }
    }

    public getTypeProperties(_type: string): IPropertyType {
        const type = {} as IPropertyType;

        switch (_type) {
            case "electricity":
                type.name = "e_elec";
                type.iconBop = "icon-edponline-Bop-001";
                type.iconBra = "icon-edponline-Bra-001";
                break;
            case "gas":
                type.name = "e_gas";
                type.iconBop = "icon-edponline-Bop-009";
                type.iconBra = "icon-edponline-Bra-009";
                break;
            case "dual":
                type.name = "e_gas_elec";
                type.iconBop = "icon-edponline-Bop-003";
                type.iconBra = "icon-edponline-Bra-003";
                break;
            default:
                break;
        }

        return type;
    }

    public getPackProperties(_pack: HasPackModel): object {
        const pack = {
            name: "",
            classColor: "",
        };
        switch (true) {
            case _pack.easy:
                pack.name = "pt.edp.edponline.pack-component.packeasy";
                pack.classColor = "color-easy";
                break;
            case _pack.smart:
                pack.name = "pt.edp.edponline.pack-component.packsmart";
                pack.classColor = "color-smart";
                break;
            case _pack.full:
                pack.name = "pt.edp.edponline.pack-component.packfull";
                pack.classColor = "color-full";
                break;
        }
        return pack;
    }

    public isActivo(): boolean {
        if (this.Status === "pt.edp.edponline.state.ACTIVO") {
            return true;
        }
        return false;
    }

    public isInactive(): boolean {
        if (this.Status === "pt.edp.edponline.state.INACTIVO") {
            return true;
        }
        return false;
    }

    public isInActivation(): boolean {
        if (this.Status === "pt.edp.edponline.state.EMACTIVACAO") {
            return true;
        }
        return false;
    }

    public isSmallBusiness(): boolean {
        return !this.Attributes.ZCM_ATR_0134 || this.Attributes.ZCM_ATR_0134 !== "11000";
    }

    public notSmallBusiness(): boolean {
        return Boolean(this.Attributes.ZCM_ATR_0134) && this.Attributes.ZCM_ATR_0134 === "11000";
    }

    public hasGreenEnergy(): boolean {
        return this.Attributes.ZCM_ATR_0229 && this.Attributes.ZCM_ATR_0229 === "VERDE" ? true : false;
    }

    public hasCampaign(campaign: string): boolean {
        return this.Attributes.ZCM_ATR_0017 && this.Attributes.ZCM_ATR_0017.toUpperCase() === campaign.toUpperCase();
    }

    public isManaged(): boolean {
        return this.IsManaged;
    }

    public getTariff(basicComponent: BasicComponent): string {
        return basicComponent.Tariff;
    }

    public getVoltageLevel(basicComponent: BasicComponent): string {
        return basicComponent.VoltageLevel;
    }

    public getPower(basicComponent: BasicComponent): number {
        return basicComponent.Power ? parseFloat(basicComponent.Power.split(" ")[0].replace(",", ".")) : 0;
    }

    public hasAvailableTariff(basicComponent: BasicComponent) {
        const AVAILABLE_TARIFS = ["1", "2", "D", "S"];
        if (AVAILABLE_TARIFS.includes(basicComponent.Tariff)) {
            return true;
        }
        return false;
    }

    public isElegiblePack(basicComponent: BasicComponent, user: EdpUser) {
        const tariff = this.hasAvailableTariff(basicComponent);
        const powerLevel = this.getPower(basicComponent);
        if (
            !this.isInactive() &&
            !this.HasPack &&
            !this.HasServiceFuncionaCA &&
            !this.HasM &&
            (!this.Attributes.ZCM_ATR_0134 || this.Attributes.ZCM_ATR_0134 === "11000") &&
            !(basicComponent.SupplyType === "E" && (powerLevel > 20.7 || powerLevel < 3.45)) &&
            tariff &&
            !(this.HasG && !this.HasE) &&
            !(this.isManaged() && user.nif !== this.OwnerNif)
        ) {
            return true;
        }
        return false;
    }

    public isIndexed() {
        return this.HasIndexedOfferE || this.HasIndexedOfferG;
    }

    public hasSolar(): boolean {
        return this.Attributes.ZCM_ATR_0218 && this.Attributes.ZCM_ATR_0218.toUpperCase() !== "NA";
    }

    public get hasWallbox(): boolean {
        return !!this.Product.find(product => product.ProductCodes.some(code => code.includes("WALBOX") || code === "MOBIE04CS"));
    }

    public get hasCEME(): boolean {
        return !!this.Product.find(product => product.ProductCodes.some(code => code.includes("MOBIE")));
    }

    public get hasSolarBattery(): boolean {
        return !!this.Product.find(product => product.ProductCodes.some(code => code.includes("SOLBAT")));
    }

    public isIndexedOffer(): boolean {
        return this.HasIndexedOfferE || this.HasIndexedOfferG;
    }

    public isMonthlyInvoice(): boolean {
        return this.InvoicePeriodicity === InvoicePeriodicityEnum.MONTHLY;
    }
}

export class ContractFactory {
    static create(contract: Contract) {
        return new Contract(
            contract.Avatar,
            contract.CustomerFirstName,
            contract.CustomerLastName,
            contract.ElectronicInvoiceEmail,
            contract.DirectDebitIban,
            contract.Company,
            contract.Nickname,
            contract.ResourceId,
            contract.AccountId,
            contract.BasicComponent,
            contract.Product,
            contract.HasE,
            contract.HasG,
            contract.HasServiceFunciona,
            contract.OfferId,
            contract.edpPlus,
            contract.HasDirectDebit,
            contract.HasDynamicOfferE,
            contract.HasElectronicInvoice,
            contract.ContractId,
            contract.ContractNr,
            contract.IsManaged,
            contract.OwnerNif,
            contract.PhoneNumber,
            contract.HasContaCerta,
            contract.ContaCertaValue,
            contract.HasServiceFaturaSegura,
            contract.Disabled,
            contract.BillingAddress,
            contract.Status,
            contract.HasPack,
            contract.PackCode,
            contract.HasPackInActivation,
            contract.Attributes,
            contract.HasM,
            contract.HasServiceFuncionaCA,
            contract.AccountNumber,
            contract.EmployeeBenefit,
            contract.IsManagedBy,
            contract.IsManagedByMe,
            contract.HasHealth,
            contract.HasIndexedOfferE,
            contract.HasIndexedOfferG,
            contract.HasSolar,
            contract.Ird_start,
            contract.Ird_end,
            contract.Ird_update,
            contract.BankName,
            contract.CO2Info,
            contract.HasRedy,
            contract.SmartNow,
            contract.Telemetering,
            contract.UpdateInProgress,
            contract.CreationDate,
            contract.InvoicePeriodicity,
            contract.ContractOperation,
            contract.Mandate
        );
    }
}

export interface IFilteredContractList {
    filtered: Contract[];
    total: number;
}

export interface IContractFilter {
    id: number;
    name: string;
    options: FilterOption[];
    hasOptionSelected: boolean;
    clicked: boolean;
}

export interface FilterOption {
    id: number;
    name: string;
    value: string;
    selected: boolean;
}

export class ContractFilter {
    constructor(
        public id: number,
        public name: string,
        public options: FilterOption[],
        public hasOptionSelected: boolean = false,
        public clicked: boolean = false
    ) {}
}

export class ContractFiltersFactory {
    static generateContractFilters() {
        const energyOptions: FilterOption[] = [
            {
                id: 0,
                name: "pt.edp.edponline.contract.list.filter.energy.electricity",
                value: "electricity",
                selected: false,
            },
            {
                id: 1,
                name: "pt.edp.edponline.contract.list.filter.energy.gas",
                value: "gas",
                selected: false,
            },
            {
                id: 2,
                name: "pt.edp.edponline.contract.list.filter.energy.dual",
                value: "dual",
                selected: false,
            },
        ];
        const statusOptions: FilterOption[] = [
            {
                id: 1,
                name: "pt.edp.edponline.contract.list.filter.status.active",
                value: "active",
                selected: true,
            },
            {
                id: 2,
                name: "pt.edp.edponline.contract.list.filter.status.inactive",
                value: "inactive",
                selected: false,
            },
        ];

        const ftrEnergy = new ContractFilter(1, "pt.edp.edponline.contract.list.filter.energy", energyOptions, false);
        const ftrStatus = new ContractFilter(2, "pt.edp.edponline.contract.list.filter.status", statusOptions, true);

        return [ftrEnergy, ftrStatus];
    }
}

export interface ContractChange {
    ContractId: string;
    Operation: string;
    Content: object | ContractPhoneUpdate;
}

export interface ContractPhoneUpdate {
    OldValue: string;
    NewValue: string;
}

export interface ContractTariffsMapping {
    cicle: {};
    cycleTariffPowerMapping: CycleTariffPowerMapping;
    hourlyDiscr: {};
    hourlyDiscrCicleRelation: {};
    hourlyDiscrUpload: {};
    hourlyDiscrUploadInverted: {};
    power: {};
    powerEDE: {};
    powerEDEInverted: {};
}

export interface CycleTariffPowerMapping {
    ElectricityBilling: Tariff[];
}

export interface Tariff {
    Power: string;
    Tariff: {};
}

export interface ContractNewTariffs {
    power: string;
    tariff: string;
    cycle: string;
}

export interface ContractDiscounts {
    pack: PackDiscount[];
    power: PowerDiscount[];
    paymentCondition: PaymentConditionDiscount[];
}

export interface PackDiscount {
    electDiscount: string;
    gasDiscount: string;
    pack: string;
    type: number;
}

export interface PowerDiscount {
    currentCycle: string;
    currentHourOption: string;
    electDiscount: string;
    gasDiscount: string;
}

export interface PaymentConditionDiscount {
    hasDDirect: boolean;
    hasEInvoice: boolean;
    electDiscount: string;
    gasDiscount: string;
}

export interface ContractedService {
    type: string;
    date: string;
    status: boolean;
    code: string[];
    pack: string;
}

export interface WallboxService {
    hasCEME: boolean;
    hasWallbox: boolean;
}

export enum InvoicePeriodicityEnum {
    ANNUAL = "pt.edp.edponline.periodicity.ANNUAL",
    MONTHLY = "pt.edp.edponline.periodicity.MONTHLY",
    BIMONTHLY = "pt.edp.edponline.periodicity.BIMONTHLY",
}

export interface Mandate {
    ID: string;
    Country: string;
    Key: string;
    AccountNumber: string;
    ControlKey: string;
    IBAN: string;
    ValidFrom: string;
    ValidTo: string;
    MaxAmount: number;
}

export interface SEPAUpdate {
    ContractId: string;
    Operation: SEPAUpdateOperation;
    Content: {
        NewValue: {
            validTo: string;
            maxAmount: string;
        };
    };
}

export enum SEPAUpdateOperation {
    CHANGE_SEPA_LIMIT = "CHANGE_SEPA_LIMIT",
    CHANGE_SEPA_LIMIT_CANCEL = "CHANGE_SEPA_LIMIT_CANCEL",
}
